/**
 * Guides and articles. Static data, no CMS.
 *
 * Why static: the Firestore-backed blog had zero posts and no working admin API
 * (every /api/admin/* route 404s), so there was nothing to preserve. Static
 * articles are prerendered by construction, versioned in git, and cannot be
 * lost to a broken backend. scripts/routes.mjs reads the slugs from this file,
 * so adding an article here automatically adds it to the prerender, the
 * sitemap and llms.txt.
 *
 * ── FOR ANDY ─────────────────────────────────────────────────────────────────
 * Every claim about NT law or regulation is marked with a `VERIFY:` comment
 * above the block it appears in, with the source it came from. Please confirm
 * each one before this goes out — these are written from public nt.gov.au
 * material, not from your operational knowledge, and you are the expert.
 * The `sources` array on each article is rendered on the page: citing primary
 * sources is both honest and a strong signal to answer engines.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Content block types: paragraph | heading | list | callout
 */

const AUTHOR = "Aussie Inspections";
const TODAY = "2026-09-27";

const articles = [
  // ───────────────────────────────────────────────────────────── pricing
  {
    slug: "how-much-does-a-building-inspection-cost-in-darwin",
    title: "How Much Does a Building Inspection Cost in Darwin?",
    description:
      "What a building inspection costs in Darwin and the Top End, what moves the price up or down, and what should already be included in the quote.",
    author: AUTHOR,
    publishedAt: TODAY,
    updatedAt: TODAY,
    category: "Property Buying Tips",
    readTime: "5 min read",
    image: null,
    content: [
      {
        type: "paragraph",
        text: "The short answer: inspections in Darwin and the surrounding Top End start from $260 including GST, and most residential jobs land between $260 and $600 depending on the type of report and the property. We quote a flat rate up front rather than an hourly estimate, because a price that moves after the inspection is not really a price.",
      },
      { type: "heading", text: "What the different reports cost" },
      {
        type: "paragraph",
        text: "Different reports answer different questions, so they are priced separately. A pool remediation report starts from $260. A building status report — the file check on approvals and permits — starts from $325, as does a pest report. A full building inspection report, which includes both the condition report and the remediation report, starts from $455. Plumbing reports start from $520 and electrical reports from $585, both delivered through our qualified partners.",
      },
      { type: "heading", text: "What actually moves the price" },
      {
        type: "list",
        items: [
          "Property size. A three-bedroom unit and a five-bedroom house on acreage are not the same job. More roof, more subfloor, more wet areas, more time.",
          "Location. Darwin, Palmerston and the closer Litchfield suburbs are standard rate. Wagait Beach, Dundee Beach and the Cox Peninsula involve a ferry or a long drive, and that is reflected in the quote.",
          "Complexity. Multiple structures, a granny flat, a shed with its own approval history, split levels, or restricted access under the house all add time.",
          "Bundling. If you need a building inspection and a pest report on the same property, booking them together is cheaper than booking them apart.",
        ],
      },
      { type: "heading", text: "What should already be in the price" },
      {
        type: "paragraph",
        text: "Be wary of a quote that looks low until you read the inclusions. Ours covers the inspection itself, a digital report with photographs of every issue identified, and delivery within 72 hours. Building condition reports also include a remediation report — estimated costs to address what we found — at no extra charge, because a list of defects without any sense of what they cost to fix is only half an answer.",
      },
      {
        type: "callout",
        text: "A cheaper inspection that misses an unapproved patio, or that arrives after your contract cooling-off period has closed, is not cheaper.",
      },
      { type: "heading", text: "Is it worth it?" },
      {
        type: "paragraph",
        text: "On a Darwin median house price, an inspection is a fraction of a percent of the purchase price. The usual outcome is not a dramatic discovery that kills the sale. It is a handful of documented, photographed issues that either give you room to renegotiate or give you the confidence to proceed without wondering. For sellers, the same report reduces the chance of a buyer's inspector finding something you did not know about at the worst possible moment.",
      },
    ],
    faq: [
      {
        q: "How much is a building inspection in Darwin?",
        a: "From $260 including GST. A full building inspection report, which includes both the condition report and the remediation report, starts from $455. Pricing is flat rate and quoted up front.",
      },
      {
        q: "Does the price include the report?",
        a: "Yes. The quoted price covers the inspection and a digital report with photographs, delivered within 72 hours. Building condition reports also include a remediation report with estimated repair costs at no extra cost.",
      },
      {
        q: "Do you charge more outside Darwin?",
        a: "Darwin, Palmerston and the closer Litchfield areas are standard rate. Wagait Beach, Dundee Beach and Cox Peninsula properties involve a ferry crossing or a long drive, which is reflected in the quote up front.",
      },
    ],
    sources: [],
  },

  // ───────────────────────────────────────────────────────────── pool
  // VERIFY: every legislative claim in this article comes from nt.gov.au and
  // legislation.nt.gov.au — the Swimming Pool Safety Act 2004 and Swimming Pool
  // Safety Regulations 2004, the 300mm water-depth trigger, the 1 January 2003
  // threshold, the two standards (Modified Australian Standard / Community
  // Safety Standard), and the certificate-vs-written-report outcome.
  // Please confirm all of it, and confirm the Pool Fencing Unit is still the
  // administering body under that name, before publishing.
  {
    slug: "nt-pool-fencing-compliance-what-darwin-owners-need-to-know",
    title: "NT Pool Fencing Compliance: What Darwin Owners Actually Need",
    description:
      "The Northern Territory has two pool barrier standards, not one, and which applies to you depends on your block and when the pool went in. Here is how it works.",
    author: AUTHOR,
    publishedAt: TODAY,
    updatedAt: TODAY,
    category: "Pool Safety",
    readTime: "7 min read",
    image: null,
    content: [
      {
        type: "paragraph",
        text: "Pool compliance in the Northern Territory catches people out more than almost anything else in a property transaction, because the NT does not work the way most other states do. There are two different standards, and which one applies to your pool is not a matter of choice.",
      },
      { type: "heading", text: "The rules that apply" },
      {
        type: "paragraph",
        text: "Pool safety in the Territory sits under the Swimming Pool Safety Act 2004 and the Swimming Pool Safety Regulations 2004. As a rule of thumb, any pool or spa capable of holding water 300mm or more deep needs a compliant safety barrier. That catches a lot of things people do not think of as pools, including some portable and above-ground installations.",
      },
      { type: "heading", text: "Two standards, not one" },
      {
        type: "paragraph",
        text: "This is the part that surprises people. The Territory recognises two barrier standards: the Modified Australian Standard, which is the higher bar, and the Community Safety Standard, which is the lower one. Which standard your pool must meet depends on the size of the property and when the pool was installed — specifically whether it went in before or after 1 January 2003 — and on whether the property has previously been sold, transferred or leased.",
      },
      {
        type: "callout",
        text: "A pool that was legitimately compliant under one standard can fall short the moment the property changes hands, because the transaction itself can change which standard applies.",
      },
      { type: "heading", text: "Certificate or report" },
      {
        type: "paragraph",
        text: "When a pool safety barrier is assessed and meets the Modified Australian Standard, a compliance certificate is issued. If it does not meet that standard, what comes back instead is a written report setting out where it falls short. Getting that written report when you were expecting a certificate, three days before settlement, is a bad afternoon.",
      },
      { type: "heading", text: "Where most Darwin pools fail" },
      {
        type: "list",
        items: [
          "Gates that do not self-close and self-latch reliably from any position, or that have dropped on their hinges over a wet season or two.",
          "Climbable objects inside the non-climbable zone — an air-conditioning unit, a pot plant, a retaining wall, a pool pump housing, a tree that has grown since the barrier went in.",
          "Gaps under the barrier where the ground has washed out or settled, which happens readily in Top End soils after heavy rain.",
          "Boundary fences doing double duty as a pool barrier without meeting the height and non-climbable requirements on both sides.",
          "Modifications made in good faith — a new deck, a shade sail post, a pergola — that quietly breached the barrier without anyone realising.",
        ],
      },
      { type: "heading", text: "What we do about it" },
      {
        type: "paragraph",
        text: "Our pool remediation report is a pre-check, not an official certification. We inspect the barrier against the requirements, photograph and document everything that would fail, and tell you what to fix and roughly what it will cost. The point is that you go into the official inspection already knowing the answer, rather than discovering the problem when the clock is running on a contract.",
      },
      {
        type: "paragraph",
        text: "If you are buying a property with a pool in Darwin, Palmerston or Litchfield, get this done early. If you are selling, get it done before you list. A barrier issue found in week one is a tradesman's afternoon. The same issue found in the final week of a contract is leverage against you.",
      },
    ],
    faq: [
      {
        q: "Does my Darwin pool need a fence?",
        a: "As a general rule, any pool or spa in the Northern Territory capable of holding water 300mm or more deep requires a compliant safety barrier under the Swimming Pool Safety Act 2004. This includes many above-ground and portable pools.",
      },
      {
        q: "Which pool safety standard applies to my property?",
        a: "The NT recognises two standards: the Modified Australian Standard and the Community Safety Standard. Which one applies depends on the size of your property, whether the pool was installed before or after 1 January 2003, and whether the property has previously been sold, transferred or leased.",
      },
      {
        q: "What is the difference between a pool remediation report and a compliance certificate?",
        a: "A compliance certificate is the official document issued when a barrier is assessed as meeting the Modified Australian Standard. Our pool remediation report is a pre-check: we identify and photograph anything that would fail, and estimate the cost to fix it, so you can put it right before the official inspection.",
      },
      {
        q: "When should I get my pool checked before selling?",
        a: "Before you list, not after you have a contract. Barrier faults are usually quick and inexpensive to fix when there is no deadline, and expensive leverage against you when there is.",
      },
    ],
    sources: [
      {
        label: "Pool fencing and barrier safety standards — NT Government",
        url: "https://nt.gov.au/property/homeowners-and-landlords/pool-or-spa/install-a-pool-or-spa/about-pool-fencing-and-barrier-safety-standards",
      },
      {
        label: "Get a compliance certificate or acknowledgement notice — NT Government",
        url: "https://nt.gov.au/property/homeowners-and-landlords/pool-or-spa/install-a-pool-or-spa/apply-for-a-compliance-certificate-or-acknowledgement-notice",
      },
      {
        label: "Swimming Pool Safety Regulations 2004 — NT legislation",
        url: "https://legislation.nt.gov.au/api/sitecore/Act/PDF?id=12194",
      },
    ],
  },

  // ───────────────────────────────────────────────────────── status report
  // VERIFY: the claims here about Certificates of Occupancy, unapproved work
  // being unlawful to occupy, the two-year building permit expiry, and building
  // files being held by Building Advisory Services all come from nt.gov.au
  // material. Confirm the current department name and that the two-year expiry
  // still holds before publishing.
  {
    slug: "what-is-a-building-status-report-nt",
    title: "What Is a Building Status Report, and Do You Need One in the NT?",
    description:
      "A building status report checks the paperwork, not the paintwork: what has been approved on a Northern Territory property, and what has not.",
    author: AUTHOR,
    publishedAt: TODAY,
    updatedAt: TODAY,
    category: "Building Inspections",
    readTime: "6 min read",
    image: null,
    content: [
      {
        type: "paragraph",
        text: "Most people booking an inspection are thinking about condition: is the roof sound, is there movement in the slab, is the wiring safe. A building status report asks a completely different question. It asks whether the structures on the property are legally supposed to be there.",
      },
      { type: "heading", text: "What it actually involves" },
      {
        type: "paragraph",
        text: "A building status report is a file review. It means going to the building records held by the Territory's Building Advisory Services, pulling what exists for that address, and reading it against what is physically standing on the block today. Certificates of Occupancy, building permits, working drawings — and then a walk of the property to see whether anything is there that the file does not account for.",
      },
      { type: "heading", text: "Why it matters in the Top End" },
      {
        type: "paragraph",
        text: "Darwin properties accumulate structures. Carports, patios, sheds, granny flats, decks, shade structures, pool surrounds. A lot of it was built well. Some of it was built without a permit. Building work commenced without the required building permit is unlawful, and a structure in that position may not be lawfully occupied.",
      },
      {
        type: "callout",
        text: "Buying an unapproved structure means buying the problem. It does not stay with the person who built it.",
      },
      { type: "heading", text: "Expired permits are their own trap" },
      {
        type: "paragraph",
        text: "A building permit does not last forever. Permits expire two years from the date of issue, and where no Certificate of Occupancy was issued within that window, the permit lapses and is recorded as expired. That leaves a structure that was started legitimately but never properly finished on paper — which is a different problem from never having had a permit at all, and often a more surprising one, because everyone involved assumed it had been sorted.",
      },
      { type: "heading", text: "Who should get one" },
      {
        type: "list",
        items: [
          "Buyers of any property with more than the original dwelling on it — a shed, a carport, a patio, an extension, a granny flat.",
          "Sellers who have added anything over the years and are not certain the paperwork was completed.",
          "Anyone who has inherited a property, where the build history is not personally known.",
          "Anyone refinancing or insuring a property where a lender or insurer has asked questions about approvals.",
          "Anyone planning to build on, or extend, an existing structure whose own approval status is unclear.",
        ],
      },
      { type: "heading", text: "What you get back" },
      {
        type: "paragraph",
        text: "A status report that sets out what is approved, what is not, and what is unresolved, with the documentation behind each finding. It does not assess condition — that is a building condition report, and the two are often booked together. What it gives you is certainty about the legal position before you commit, which is exactly the thing that is hardest to establish on your own and most expensive to get wrong.",
      },
    ],
    faq: [
      {
        q: "What is a building status report?",
        a: "A review of the official building records for a property — Certificates of Occupancy, building permits and working drawings held by Building Advisory Services — checked against what is physically on the site, to identify any structures that were built without the required approvals.",
      },
      {
        q: "Is a building status report the same as a building inspection?",
        a: "No. A building status report checks approvals and paperwork. A building condition report assesses the physical condition of the property. They answer different questions and are often booked together.",
      },
      {
        q: "What happens if a property has an unapproved structure?",
        a: "Building work carried out without a required building permit is unlawful and the structure may not be lawfully occupied. The issue transfers with the property, so it becomes the buyer's problem to resolve.",
      },
      {
        q: "Can a building permit expire?",
        a: "Yes. Building permits expire two years from the date of issue. If no Certificate of Occupancy was issued in that time, the permit lapses and is recorded as expired.",
      },
    ],
    sources: [
      {
        label: "Building certification process — NT Government",
        url: "https://nt.gov.au/property/building/build-or-renovate-your-home/building-and-renovating-permits-and-processes/building-certification-process",
      },
      {
        label: "Residential building work that requires permits — NT Government",
        url: "https://nt.gov.au/property/building/build-or-renovate-your-home/building-and-renovating-a-home/residential-building-work-that-requires-permits",
      },
    ],
  },

  // ───────────────────────────────────────────────────────────── termites
  // VERIFY: Mastotermes darwiniensis as the dominant high-risk species in the
  // Top End, year-round activity, and the 12-month inspection interval are all
  // from public pest-industry and Houspect NT material rather than a primary
  // standard. Confirm the inspection interval you actually recommend, and
  // confirm how you want AS 3660 referenced, before publishing.
  {
    slug: "termites-in-darwin-why-the-top-end-is-different",
    title: "Termites in Darwin: Why the Top End Is a Different Problem",
    description:
      "Darwin has Australia's most destructive termite and a climate that lets it work all year. What that means for buying, owning and inspecting property here.",
    author: AUTHOR,
    publishedAt: TODAY,
    updatedAt: TODAY,
    category: "Pest & Termite",
    readTime: "6 min read",
    image: null,
    content: [
      {
        type: "paragraph",
        text: "Termite advice written for Sydney or Melbourne does not transfer to Darwin. The species are different, the season is different, and the speed is different. If you are buying property in the Top End, this is the risk that deserves the most attention and usually gets the least.",
      },
      { type: "heading", text: "The species that makes the difference" },
      {
        type: "paragraph",
        text: "The Top End is home to Mastotermes darwiniensis, the giant northern termite — named after Darwin, and widely regarded as the most destructive termite in Australia. It has an appetite well beyond the softwood most people picture. Colonies are large, aggressive, and capable of causing serious structural damage in a timeframe that surprises people used to southern conditions.",
      },
      { type: "heading", text: "There is no off-season" },
      {
        type: "paragraph",
        text: "In cooler parts of Australia, termite activity slows in winter. Here it does not. Warm temperatures and high humidity year-round mean subterranean termites can stay active continuously. The wet season compounds it: monsoonal rain saturates soil and keeps conditions damp around foundations, slab edges and subfloors, which is exactly what they want.",
      },
      {
        type: "callout",
        text: "A property can be clear at inspection and have active termites six months later. That is not a failure of the inspection — it is the nature of the risk here, and it is why interval matters as much as any single report.",
      },
      { type: "heading", text: "What to look for between inspections" },
      {
        type: "list",
        items: [
          "Mud tubes on foundations, piers, slab edges or inside the subfloor — pencil-width earthen tracks are the classic sign.",
          "Timber that sounds hollow when tapped, or skirting and architraves that give under light pressure.",
          "Doors and windows that have started sticking without any obvious movement in the structure.",
          "Paint or plaster that looks blistered or rippled in a way that does not match a leak.",
          "Discarded wings near windows and light fittings after rain, particularly early in the wet.",
          "Timber in contact with soil anywhere on the property — fence posts, garden edging, stored offcuts under the house. All of it is a bridge.",
        ],
      },
      { type: "heading", text: "Reduce the invitation" },
      {
        type: "paragraph",
        text: "Keep timber and mulch away from the base of the house. Keep the slab edge visible rather than buried under garden beds or paving. Fix leaking taps, air-conditioning condensate and blocked downpipes promptly, because a reliable moisture source next to a structure is the single best thing you can offer a colony. Keep subfloor ventilation clear.",
      },
      { type: "heading", text: "How we handle it" },
      {
        type: "paragraph",
        text: "We deliver pest and termite reports through qualified partners who work in these conditions every week, rather than treating it as an add-on to a building inspection. Given the pressure in this region, an annual inspection is the sensible baseline for most Top End properties, and more often where there is a history of activity or the construction is timber-framed.",
      },
      {
        type: "paragraph",
        text: "If you are buying, book the pest report alongside the building inspection. The building report tells you the condition of what is there. The pest report tells you whether something is currently eating it.",
      },
    ],
    faq: [
      {
        q: "Are termites bad in Darwin?",
        a: "Yes. The Top End has Mastotermes darwiniensis, the giant northern termite, widely regarded as Australia's most destructive termite species, and the warm humid climate allows subterranean termites to stay active year-round rather than slowing in winter.",
      },
      {
        q: "How often should I get a termite inspection in Darwin?",
        a: "An annual inspection is the sensible baseline for most Top End properties, and more frequently where the property has a history of termite activity, is timber-framed, or sits in a high-risk location.",
      },
      {
        q: "Do you do pest and termite inspections?",
        a: "We deliver pest and termite reports through qualified, insured partners who specialise in Top End conditions. They can be booked alongside a building inspection on the same property.",
      },
      {
        q: "What are the early signs of termites in a Darwin home?",
        a: "Mud tubes on foundations or piers, timber that sounds hollow when tapped, doors or windows that start sticking, blistered paint or plaster, and discarded wings near windows after rain early in the wet season.",
      },
    ],
    sources: [
      {
        label: "Types of termites in the tropics — Houspect NT",
        url: "https://www.houspect.com.au/nt/types-termites-tropics/",
      },
    ],
  },

  // ─────────────────────────────────────────────────────── turnaround/process
  {
    slug: "what-happens-during-a-building-inspection",
    title: "What Actually Happens During a Building Inspection",
    description:
      "How long an inspection takes, what the inspector looks at, what 'visible issues only' means, and when the report lands.",
    author: AUTHOR,
    publishedAt: TODAY,
    updatedAt: TODAY,
    category: "Building Inspections",
    readTime: "5 min read",
    image: null,
    content: [
      {
        type: "paragraph",
        text: "A building inspection is less mysterious than people expect. Here is the whole process, start to finish, so you know what you are buying and what you are not.",
      },
      { type: "heading", text: "On the day: two to three hours" },
      {
        type: "paragraph",
        text: "A typical inspection on an average-sized property takes two to three hours. Bigger or more complex properties take longer. You are welcome to be there, and if you are, the end of the inspection is the best time to ask questions — the inspector has the whole property fresh in mind.",
      },
      { type: "heading", text: "What gets looked at" },
      {
        type: "list",
        items: [
          "The exterior: walls, cladding, eaves, guttering, downpipes, visible drainage and the condition of the slab edge or piers.",
          "The roof, inspected from where it can be safely and reasonably accessed.",
          "The roof space and subfloor, where there is safe access.",
          "Interiors: walls, ceilings, floors, windows and doors, looking for movement, moisture and damage.",
          "Wet areas — kitchen, bathrooms, laundry — which in this climate are where problems show first.",
          "Outbuildings and structures on the property, including sheds, carports and patios.",
        ],
      },
      { type: "heading", text: "What 'visible issues only' means" },
      {
        type: "paragraph",
        text: "This is the most important thing to understand, and we would rather be blunt about it than have you surprised. We inspect what can be seen during a visual assessment of accessible areas. We do not cut into walls, lift flooring, or do invasive testing. We do not offer structural engineering opinions, and we do not speculate about what might be behind a surface we cannot see.",
      },
      {
        type: "callout",
        text: "That limit is not us being cautious — it is what keeps the report trustworthy. A report full of guesses about hidden conditions is worse than useless, because you cannot tell which parts to rely on.",
      },
      {
        type: "paragraph",
        text: "Where applicable, our reports are aligned with AS 4349.1, the Australian Standard for inspection of buildings. Where something is inaccessible, the report says so plainly rather than quietly omitting it.",
      },
      { type: "heading", text: "The report: within 72 hours" },
      {
        type: "paragraph",
        text: "Your digital report is delivered within 72 hours of the inspection. Every issue identified is documented and photographed, so you are not trying to decode a description — you can see it. Reports are designed to be forwarded: to your conveyancer, your agent, your builder or the other side of a negotiation.",
      },
      {
        type: "paragraph",
        text: "If you have booked a building condition report, a remediation report comes with it at no extra cost, giving estimated costs to address the issues found. That turns a list of problems into something you can actually act on or negotiate with.",
      },
    ],
    faq: [
      {
        q: "How long does a building inspection take?",
        a: "Typically two to three hours for an average-sized property. Larger or more complex properties take longer.",
      },
      {
        q: "When will I get my report?",
        a: "All reports are delivered digitally within 72 hours of the inspection.",
      },
      {
        q: "Can I attend the inspection?",
        a: "Yes. The end of the inspection is the best time to ask questions, while the inspector has the whole property fresh in mind.",
      },
      {
        q: "What does 'visible issues only' mean?",
        a: "We assess what can be seen in accessible areas during a visual inspection. We do not carry out invasive testing, cut into walls, or give structural engineering opinions, and the report states plainly where an area was inaccessible.",
      },
    ],
    sources: [],
  },

  // ───────────────────────────────────────────────────────────── sellers
  {
    slug: "pre-sale-inspection-darwin-why-sellers-go-first",
    title: "Why Darwin Sellers Should Inspect Before They List",
    description:
      "A pre-sale inspection moves the surprise from the worst moment in a negotiation to the best one: before you have a buyer.",
    author: AUTHOR,
    publishedAt: TODAY,
    updatedAt: TODAY,
    category: "Property Buying Tips",
    readTime: "5 min read",
    image: null,
    content: [
      {
        type: "paragraph",
        text: "Most sellers wait. The buyer books an inspection, the report comes back, and whatever it found becomes a negotiation you did not choose to have, at the point in the process where you have the least leverage. Inspecting first inverts that.",
      },
      { type: "heading", text: "The leverage argument" },
      {
        type: "paragraph",
        text: "An issue you discover before listing is a job you can shop around, schedule at your convenience, and pay trade rates for. The same issue discovered by a buyer's inspector during a cooling-off period is a discount request, and the discount is rarely the cost of the repair. It is the cost of the repair plus the buyer's uncertainty about what else might be wrong.",
      },
      {
        type: "callout",
        text: "Buyers do not price defects. They price doubt. A documented, already-fixed issue costs you the repair. An undocumented one costs you the repair and the doubt.",
      },
      { type: "heading", text: "What it does to the sale" },
      {
        type: "list",
        items: [
          "Fewer renegotiations, because there is less left to find.",
          "Fewer failed contracts, which in a market the size of Darwin's matters — a property that comes back on after a fallen contract carries a question mark.",
          "Faster decisions from buyers, because a report they can read up front removes the reason to hesitate.",
          "A credible answer when an agent or buyer asks about the shed, the patio or the pool.",
        ],
      },
      { type: "heading", text: "The Darwin-specific version" },
      {
        type: "paragraph",
        text: "In the Top End there are three things worth resolving before you list rather than after. Pool barrier compliance, because the standard that applies can change when a property is sold. Approvals on any structure added over the years, because unapproved work transfers to the buyer and they will price it accordingly. And pest, because in this climate a buyer's pest report is the single most likely thing to derail a contract.",
      },
      { type: "heading", text: "What it costs to go first" },
      {
        type: "paragraph",
        text: "A pre-sale inspection starts from $260 including GST, with the report back within 72 hours. Set against the cost of one renegotiation, or one contract falling over and the property sitting on the market with a history, it is not a close call.",
      },
      {
        type: "paragraph",
        text: "You can also hand the report to prospective buyers. A seller who volunteers an independent inspection report is making a statement about the property that no amount of listing copy achieves.",
      },
    ],
    faq: [
      {
        q: "Should I get a building inspection before selling my house?",
        a: "Yes, if you want to control the timing. Issues found before listing can be fixed at trade rates on your schedule. The same issues found by a buyer's inspector become a discount request at the point where you have least leverage.",
      },
      {
        q: "What should Darwin sellers check before listing?",
        a: "Pool barrier compliance, approvals for any structure added over the years, and a pest inspection. Those three account for most contract renegotiations in the Top End.",
      },
      {
        q: "Can I show my inspection report to buyers?",
        a: "Yes, and it is worth doing. An independent report volunteered by the seller builds more confidence than any listing description.",
      },
    ],
    sources: [],
  },

  // ───────────────────────────────────────────────────────────── buyers
  {
    slug: "buying-a-house-in-darwin-inspection-checklist",
    title: "Buying a House in Darwin: What to Check Before You Sign",
    description:
      "A Top End specific checklist for buyers — the reports that matter, the order to book them, and the things that catch people out here.",
    author: AUTHOR,
    publishedAt: TODAY,
    updatedAt: TODAY,
    category: "Property Buying Tips",
    readTime: "6 min read",
    image: null,
    content: [
      {
        type: "paragraph",
        text: "Buying in Darwin has a few local hazards that a generic buyer's checklist will not mention. Here is what to do, in what order.",
      },
      { type: "heading", text: "Book early, not late" },
      {
        type: "paragraph",
        text: "The single most common mistake is leaving inspections until the contract is signed and the clock is ticking. Reports take up to 72 hours to come back, and if a report surfaces something that needs a second opinion — a pest treatment quote, a builder's view on a repair — you need room to get it. Book as soon as you are serious about a property.",
      },
      { type: "heading", text: "The three reports that matter here" },
      {
        type: "list",
        items: [
          "Building condition report — the physical state of the property. Ours includes a remediation report with estimated repair costs at no extra charge, which is what turns findings into a negotiating position.",
          "Pest and termite report — non-negotiable in the Top End. Mastotermes darwiniensis is active year-round in this climate and causes damage fast.",
          "Building status report — the approvals check. If the property has a shed, patio, carport, deck or granny flat, you want to know whether the paperwork exists before you own the problem.",
        ],
      },
      { type: "heading", text: "Pool? Add a fourth" },
      {
        type: "paragraph",
        text: "If there is a pool or spa, get the barrier assessed. The NT runs two different pool safety standards and which one applies can change when a property is sold, transferred or leased. A barrier that has been fine for the current owner for a decade is not automatically fine for you.",
      },
      { type: "heading", text: "Things to look at yourself at the inspection" },
      {
        type: "list",
        items: [
          "Where does water go in a downpour? Look at the fall of the ground away from the house, the state of the downpipes, and whether the slab edge is buried under garden beds.",
          "Air-conditioning. In Darwin it is not a luxury item. Check age, condition and whether the condensate drains somewhere sensible rather than against a wall.",
          "Signs of wet season water ingress — staining at the top of walls, along window heads, or around ceiling penetrations.",
          "Corrosion on anything metal near the coast. Salt air in the Top End is unforgiving on fixings, brackets, gutters and roof screws.",
          "Every structure that is not the original house, and whether the vendor can say when it went up and who approved it.",
        ],
      },
      {
        type: "callout",
        text: "Ask the agent directly, in writing: has any structure on this property been built without a permit? The answer, or the absence of one, is informative either way.",
      },
      { type: "heading", text: "Reading the report" },
      {
        type: "paragraph",
        text: "A report with findings in it is normal. Almost no property comes back perfectly clean, and a report that does should make you curious rather than relaxed. What matters is the difference between maintenance items you will get to over the years and structural or compliance issues that need resolving before settlement. If that distinction is not obvious to you, ring us — we would rather spend ten minutes on the phone than have you misread it in either direction.",
      },
    ],
    faq: [
      {
        q: "What inspections do I need when buying a house in Darwin?",
        a: "A building condition report, a pest and termite report, and — if there is any structure beyond the original dwelling — a building status report to confirm approvals. Add a pool barrier assessment if the property has a pool or spa.",
      },
      {
        q: "When should I book a building inspection when buying?",
        a: "As soon as you are serious about the property. Reports take up to 72 hours, and you want room to get a second opinion or a repair quote before any contract deadline.",
      },
      {
        q: "What is specific to buying property in the Top End?",
        a: "Year-round termite pressure, pool barrier standards that can change when a property is sold, unapproved structures accumulated over the years, salt-air corrosion near the coast, and wet season water ingress.",
      },
    ],
    sources: [],
  },

  // ───────────────────────────────────────────────────────────── wet season
  {
    slug: "wet-season-what-darwin-humidity-does-to-a-house",
    title: "Wet Season: What Darwin's Climate Does to a House",
    description:
      "Humidity, monsoonal rain and salt air work on a Top End building all year. What to watch, what to fix early, and what it means for inspection timing.",
    author: AUTHOR,
    publishedAt: TODAY,
    updatedAt: TODAY,
    category: "Maintenance Advice",
    readTime: "6 min read",
    image: null,
    content: [
      {
        type: "paragraph",
        text: "Buildings in the Top End age differently. It is not just that it rains hard — it is the combination of sustained humidity, intense seasonal rainfall, and salt air, working on the same structure without much of a break.",
      },
      { type: "heading", text: "Water finds the path" },
      {
        type: "paragraph",
        text: "Monsoonal rain does not fall gently and it does not fall straight. Wind-driven rain gets into things that cope perfectly well with vertical water: window heads, door reveals, roof penetrations, the junction between a patio roof and the house wall. A gutter or downpipe sized for average rainfall is not sized for a Darwin downpour, and once water overshoots, it goes down the wall and into the ground right against the footing.",
      },
      { type: "heading", text: "What sustained humidity does" },
      {
        type: "list",
        items: [
          "Mould on ceilings, in wardrobes, behind furniture against external walls, and anywhere with poor air movement.",
          "Timber movement — doors and drawers that bind in the wet and free up in the dry.",
          "Corrosion of fixings, hinges, brackets, roof screws and gutter fasteners, accelerated hard by salt air near the coast.",
          "Deterioration of sealants and flashings, which are doing more work here than in a temperate climate.",
          "Subfloor damp, which is also an open invitation to termites.",
        ],
      },
      { type: "heading", text: "Before the wet" },
      {
        type: "paragraph",
        text: "The build-up is the time to do this, not the middle of a storm. Clear gutters and downpipes completely. Check that stormwater discharges well away from the building rather than into a garden bed at the base of the wall. Check roof screws and flashings. Cut back vegetation touching the roof or walls. Check that the ground still falls away from the house — it moves over time, particularly where soil has washed out.",
      },
      {
        type: "callout",
        text: "Almost every expensive water problem we document started as a blocked downpipe or a ground level that crept up above the slab edge. Both are an afternoon's work to prevent.",
      },
      { type: "heading", text: "After the wet" },
      {
        type: "paragraph",
        text: "The end of the wet is the most revealing time to inspect a Top End property, because the building has just been tested. Staining that is invisible in September is obvious in April. If you are buying at the end of the wet, that is an advantage — take it.",
      },
      { type: "heading", text: "What we look for" },
      {
        type: "paragraph",
        text: "In a Darwin inspection, water and its consequences are the through-line: where it has got in, where it is sitting, what it has corroded, and what it has invited. We photograph and document what is visible in accessible areas. Where an area cannot be safely accessed, the report says so rather than leaving you to assume it was checked.",
      },
    ],
    faq: [
      {
        q: "When is the best time to get a building inspection in Darwin?",
        a: "Any time, but the end of the wet season is the most revealing — the building has just been through its hardest test, so water ingress and drainage problems are visible that would be invisible in the dry.",
      },
      {
        q: "What should I check before the wet season?",
        a: "Clear gutters and downpipes, confirm stormwater discharges away from the building, check roof screws and flashings, cut back vegetation touching the house, and confirm the ground still falls away from the slab edge.",
      },
      {
        q: "Why does salt air matter in Darwin?",
        a: "Coastal salt air accelerates corrosion of roof screws, gutter fasteners, hinges, brackets and other fixings. It is one of the main reasons Top End buildings need more frequent attention to metal components than buildings inland.",
      },
    ],
    sources: [],
  },

  // ───────────────────────────────────────────────────── report types compare
  {
    slug: "which-inspection-report-do-i-need",
    title: "Which Inspection Report Do You Actually Need?",
    description:
      "Condition, status, pest, pool, plumbing or electrical — a plain comparison of what each report answers, so you book the right one.",
    author: AUTHOR,
    publishedAt: TODAY,
    updatedAt: TODAY,
    category: "Building Inspections",
    readTime: "5 min read",
    image: null,
    content: [
      {
        type: "paragraph",
        text: "People often book the wrong report, or book one when they needed two. The reports are not tiers of thoroughness — they answer genuinely different questions.",
      },
      { type: "heading", text: "Building condition report" },
      {
        type: "paragraph",
        text: "Answers: what physical condition is this property in? A visual assessment of accessible areas, documented and photographed. Ours includes a remediation report with estimated costs at no extra charge. This is the one most buyers mean when they say 'building inspection'. From $455.",
      },
      { type: "heading", text: "Building status report" },
      {
        type: "paragraph",
        text: "Answers: is everything on this property legally approved? A file review of the official building records against what is physically on site, identifying unapproved structures and expired permits. It says nothing about condition. From $325.",
      },
      { type: "heading", text: "Pre-sale property inspection" },
      {
        type: "paragraph",
        text: "Answers: what will a buyer's inspector find? Same visual assessment, framed for a seller preparing to list, so you can fix or disclose on your own timetable instead of theirs.",
      },
      { type: "heading", text: "Pest report" },
      {
        type: "paragraph",
        text: "Answers: is anything eating this property right now, and what is the risk? Delivered through qualified partners. In the Top End this is not optional for a purchase. From $325.",
      },
      { type: "heading", text: "Pool remediation report" },
      {
        type: "paragraph",
        text: "Answers: would this pool barrier pass? A pre-check against the requirements, identifying what would fail and what it costs to fix, before the official compliance inspection. From $260.",
      },
      { type: "heading", text: "Plumbing and electrical reports" },
      {
        type: "paragraph",
        text: "Answers: what is the condition of the services specifically? Plumbing from $520, electrical from $585 through our qualified partners. Worth adding where the property is older, has been extended, or where the condition report flags something in either system.",
      },
      {
        type: "callout",
        text: "Buying, with a shed and a pool, in Darwin? That is a condition report, a status report, a pest report and a pool check. Booked together it is cheaper than booked separately, and it arrives as one picture rather than four.",
      },
      { type: "heading", text: "The common combinations" },
      {
        type: "list",
        items: [
          "Buyer, standard house: condition report + pest report.",
          "Buyer, property with added structures: condition + pest + status report.",
          "Buyer, property with a pool: add the pool remediation report.",
          "Seller preparing to list: pre-sale inspection, plus a pool check and a status report if either applies.",
          "Owner, no transaction: pest report annually; condition report if you are planning works or have noticed something.",
        ],
      },
    ],
    faq: [
      {
        q: "What is the difference between a building condition report and a building status report?",
        a: "A condition report assesses the physical state of the property. A status report reviews the official building records to confirm whether the structures on site were properly approved. They answer different questions and are often booked together.",
      },
      {
        q: "Do I need a pest report as well as a building inspection?",
        a: "In the Top End, yes. A building inspection assesses condition; a pest report identifies active termite activity and risk. Darwin's year-round termite pressure makes the pest report essential on any purchase.",
      },
      {
        q: "Can I book multiple reports at once?",
        a: "Yes, and it is cheaper than booking them separately. It also means you receive one coherent picture of the property rather than several unconnected documents.",
      },
    ],
    sources: [],
  },
];

export default articles;
export const categories = [...new Set(articles.map((a) => a.category))].sort();
export const getArticle = (slug) => articles.find((a) => a.slug === slug);
